import { openMessage } from "utils/misc";
import { useContext, useEffect, useRef } from "react";
import {
  endOnboardingJourney,
  JourneyStep,
  useOnboardingAnalytics,
} from "analytics/onboarding";
import { getOnboardingJourney } from "analytics/journey";
import { useHistory, useLocation } from "react-router-dom";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import { useDispatch, useSelector } from "react-redux";
import { ONBOARDING_STEP_KEY } from "@shared/features/onboarding/constants";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import localStorageService from "utils/localStorageService";
import {
  changeOnboardingStep,
  getOnboardingResumePath,
  clearOnboardingResumePath,
  saveOnboardingResumePath,
} from "features/onboarding/common/utils";
import { PATH_TO_DETAILED_STEP } from "features/onboarding/common/constants";
import useSharedOnboardingSteps from "@shared/features/onboarding/hooks/useSharedOnboardingSteps";
import { sharedLib } from "@shared/services/shared.service";
import { updateStoreUserIsOnboarding } from "@shared/features/onboarding/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces, WorkspaceType } from "@shared/types";
import useSharedWorkspace from "@shared/features/workspaces/useSharedWorkspace";
import { workspaceToDefaultPath } from "common/Home/General/constants";
import { QUICK_ONBOARDING_KEY } from "features/quickOnboarding/constants";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";

const useOnboardingSteps = () => {
  const track = useOnboardingAnalytics();
  const finishing = useRef(false);
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const location = useLocation();
  const path = location.pathname;
  const isGuided = new URLSearchParams(location.search).get("guided") === "1";
  const hasIntent = new URLSearchParams(location.search).has("method");
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const { getUpdatedPath } = useHistoryWithQuery();
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};
  const history = useHistory();
  const dispatch = useDispatch();
  const { setWorkspaceType, isWorkspaceValid } = useSharedWorkspace({
    allowAdminWorkspace: true,
  });

  const changeStep = (
    step: OnboardingDetailedStep,
    queryToAppend?: Record<string, string>
  ) => {
    if (!isExternalConnectAccount && user && !hasIntent) {
      const savedQuery = new URLSearchParams(
        getOnboardingResumePath(user.id)?.split("?")[1]
      );
      if (savedQuery.has("method")) {
        queryToAppend = { ...Object.fromEntries(savedQuery), ...queryToAppend };
      }
    }
    if (hasIntent && !queryToAppend) queryToAppend = {};
    if (user || isExternalConnectAccount) {
      changeOnboardingStep(
        user?.id,
        step,
        history.push,
        getUpdatedPath,
        isGuided && step !== OnboardingDetailedStep.SELECT_EXCHANGE
          ? { guided: "1", ...queryToAppend }
          : hasIntent && step !== OnboardingDetailedStep.SELECT_EXCHANGE
          ? { ...queryToAppend }
          : queryToAppend,
        !isExternalConnectAccount
      );
    }
  };

  const detailedStep = PATH_TO_DETAILED_STEP[path];
  useEffect(() => {
    if (
      !user?.is_onboarding ||
      isExternalConnectAccount ||
      !hasIntent ||
      !detailedStep
    )
      return;
    saveOnboardingResumePath(user.id, path + location.search);
  }, [
    user?.id,
    user?.is_onboarding,
    isExternalConnectAccount,
    hasIntent,
    detailedStep,
    path,
    location.search,
  ]);
  const {
    backButtonTitle: backButtonTitleKey,
    onBackButtonClick: onSharedBackButtonClick,
    finishOnboardingLoading,
    finishOnboarding,
    showBackButton,
    highLevelStep,
  } = useSharedOnboardingSteps({ changeStep, step: detailedStep });

  // The back arrow and the browser's back both come here.
  const onBackButtonClick = () => {
    track("Back Clicked", {
      from_step: getOnboardingJourney().last_step as JourneyStep | undefined,
    });
    return goBack();
  };
  const goBack = () => {
    if (new URLSearchParams(location.search).get("setup") === "1") {
      // A customized preset goes back to that preset's confirmation.
      if (new URLSearchParams(location.search).get("botUUID"))
        changeStep(OnboardingDetailedStep.DCA_BOT_OVERVIEW, { setup: "" });
      else changeStep(OnboardingDetailedStep.TRADING_METHOD, { setup: "" });
      return true;
    }
    if (
      detailedStep === OnboardingDetailedStep.COPY_DCA_BOT &&
      new URLSearchParams(location.search).get("browse") === "1"
    ) {
      changeStep(OnboardingDetailedStep.COPY_DCA_BOT, { browse: "" });
      return true;
    }
    if (detailedStep === OnboardingDetailedStep.SELECT_EXCHANGE) {
      const query = new URLSearchParams(location.search);
      const key =
        query.get("connectLive") === "1"
          ? "connectLive"
          : query.get("startMode") === "1"
          ? "startMode"
          : undefined;
      if (key) {
        query.delete(key);
        history.replace({ ...location, search: query.toString() });
        return true;
      }
    }
    if (isGuided && detailedStep === OnboardingDetailedStep.CONNECT_EXCHANGE) {
      if (new URLSearchParams(location.search).get("changePractice") === "1") {
        changeStep(
          new URLSearchParams(location.search).get("method") === "dca"
            ? OnboardingDetailedStep.COPY_DCA_BOT
            : OnboardingDetailedStep.TRADING_METHOD,
          { changePractice: "", prepareDemo: "" }
        );
        return true;
      }
      changeStep(OnboardingDetailedStep.SELECT_EXCHANGE, {
        method: new URLSearchParams(location.search).get("method") || "dca",
        startMode: "1",
        connectLive: "",
        guided: "0",
      });
      return true;
    }
    if (
      isGuided &&
      [
        OnboardingDetailedStep.COPY_DCA_BOT,
        OnboardingDetailedStep.TRADING_METHOD,
      ].includes(detailedStep)
    ) {
      changeStep(OnboardingDetailedStep.SELECT_EXCHANGE, {
        startMode: "1",
        guided: "0",
        connectLive: "",
        prepareDemo: "",
      });
      return true;
    }
    if (
      hasIntent &&
      [
        OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP,
        OnboardingDetailedStep.COPY_DCA_BOT,
      ].includes(detailedStep)
    ) {
      changeStep(OnboardingDetailedStep.SELECT_EXCHANGE, {
        method: new URLSearchParams(location.search).get("method")!,
        connectLive: "",
      });
      return true;
    }
    return onSharedBackButtonClick();
  };
  const backButtonTitle =
    new URLSearchParams(location.search).get("setup") === "1" ||
    (detailedStep === OnboardingDetailedStep.COPY_DCA_BOT &&
      new URLSearchParams(location.search).get("browse") === "1")
      ? t("intent.how_it_works")
      : isGuided && detailedStep === OnboardingDetailedStep.CONNECT_EXCHANGE
      ? t(
          new URLSearchParams(location.search).get("changePractice") === "1"
            ? "intent.how_it_works"
            : "intent.mode_back"
        )
      : isGuided &&
        [
          OnboardingDetailedStep.COPY_DCA_BOT,
          OnboardingDetailedStep.TRADING_METHOD,
        ].includes(detailedStep)
      ? t("intent.mode_back")
      : hasIntent &&
        [
          OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP,
          OnboardingDetailedStep.COPY_DCA_BOT,
        ].includes(detailedStep)
      ? t(
          new URLSearchParams(location.search).get("startMode") === "1"
            ? "intent.mode_back"
            : "intent.change_method"
        )
      : backButtonTitleKey
      ? t(backButtonTitleKey)
      : undefined;

  useEffect(() => {
    const unblock = history.block((_location, action) => {
      if (action === "POP") return false;
    });
    const handleBackButton = () => onBackButtonClick();
    window.addEventListener("popstate", handleBackButton);

    return () => {
      unblock();
      window.removeEventListener("popstate", handleBackButton);
    };
  }, [onBackButtonClick]);

  const onFinishOnboarding = (
    nextPath?: string,
    queryToAppend?: Record<string, string>,
    onSuccess?: () => void,
    skipped = false
  ) => {
    if (finishing.current) return;
    finishing.current = true;
    finishOnboarding()
      .unwrap()
      .then(() => {
        if (!skipped) track("Completed");
        localStorageService.removeValue(ONBOARDING_STEP_KEY);
        if (user) clearOnboardingResumePath(user.id);
        const updatedPath = getUpdatedPath(
          queryToAppend,
          [
            "groupID",
            "botUUID",
            "category",
            "groupRank",
            "botRank",
            "guided",
            "method",
            "exchange",
            "customDemo",
            "startMode",
            "connectLive",
            "setup",
            "channelTab",
            "account-id",
            "prepareDemo",
            "changePractice",
            "preset",
            "browse",
          ],
          nextPath
        );
        dispatch(updateStoreUserIsOnboarding(false));
        onSuccess?.();
        // After the skip's own event (in onSuccess), which still belongs to
        // this attempt.
        if (skipped) endOnboardingJourney();
        history.push(updatedPath || "/");
      })
      .catch(() => {
        track(
          "Step Failed",
          skipped
            ? { step: "skip", error_code: "skip_failed" }
            : { step: "completion", error_code: "completion_failed" }
        );
        openMessage(t("intent.finish_error"), "error");
      })
      .finally(() => {
        finishing.current = false;
      });
  };

  const onSkipOnboarding = (onSkipped?: () => void): void => {
    if (finishing.current) return;
    const workspace = isWorkspaceValid(WorkspaceType.ADMIN)
      ? WorkspaceType.ADMIN
      : WorkspaceType.USER;
    onFinishOnboarding(
      workspaceToDefaultPath[workspace],
      undefined,
      () => {
        localStorageService.setValue(QUICK_ONBOARDING_KEY, true);
        setWorkspaceType(workspace);
        onSkipped?.();
      },
      true
    );
  };

  return {
    detailedStep,
    changeStep,
    backButtonTitle,
    onBackButtonClick,
    onFinishOnboarding,
    finishOnboardingLoading,
    onSkipOnboarding,
    showBackButton:
      showBackButton ||
      new URLSearchParams(location.search).get("setup") === "1" ||
      (isGuided && detailedStep === OnboardingDetailedStep.TRADING_METHOD),
    highLevelStep,
  };
};

export default useOnboardingSteps;
